import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

export type AppRole = "adm" | "atualizador" | "visualizador";
export type UserStatus = "pending" | "approved" | "rejected";

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  status: UserStatus;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  loading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const HEARTBEAT_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  const sessionRowId = useRef<string | null>(null);
  const heartbeatTimer = useRef<number | null>(null);
  const trackedUserId = useRef<string | null>(null);

  const loadProfile = async (uid: string) => {
    const [{ data: p }, { data: r }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", uid),
    ]);
    setProfile(p as Profile | null);
    setRoles(((r ?? []) as { role: AppRole }[]).map((x) => x.role));
  };

  const startSessionTracking = async (uid: string) => {
    if (trackedUserId.current === uid && sessionRowId.current) return;
    trackedUserId.current = uid;
    try {
      const { data, error } = await supabase
        .from("user_sessions")
        .insert({ user_id: uid })
        .select("id")
        .single();
      if (error) throw error;
      sessionRowId.current = data.id;
    } catch {
      sessionRowId.current = null;
    }
    if (heartbeatTimer.current) window.clearInterval(heartbeatTimer.current);
    heartbeatTimer.current = window.setInterval(() => {
      if (!sessionRowId.current) return;
      supabase
        .from("user_sessions")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("id", sessionRowId.current);
    }, HEARTBEAT_MS);
  };

  const endSessionTracking = async () => {
    if (heartbeatTimer.current) {
      window.clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
    const id = sessionRowId.current;
    sessionRowId.current = null;
    trackedUserId.current = null;
    if (id) {
      await supabase
        .from("user_sessions")
        .update({ ended_at: new Date().toISOString(), last_seen_at: new Date().toISOString() })
        .eq("id", id);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        const uid = s.user.id;
        setTimeout(() => {
          loadProfile(uid);
          if (event === "SIGNED_IN" || event === "INITIAL_SESSION") startSessionTracking(uid);
        }, 0);
      } else {
        setProfile(null);
        setRoles([]);
        endSessionTracking();
      }
    });

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        const uid = s.user.id;
        loadProfile(uid).finally(() => setLoading(false));
        startSessionTracking(uid);
      } else setLoading(false);
    });

    const onUnload = () => {
      // best-effort: heartbeat already keeps last_seen_at recent
    };
    window.addEventListener("beforeunload", onUnload);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("beforeunload", onUnload);
      if (heartbeatTimer.current) window.clearInterval(heartbeatTimer.current);
    };
  }, []);

  const signOut = async () => {
    await endSessionTracking();
    await supabase.auth.signOut();
  };
  const refresh = async () => {
    if (user) await loadProfile(user.id);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, roles, loading, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
